import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const PerformanceChart = ({ performanceData, benchmarkData }) => {
  const [timeframe, setTimeframe] = useState('30d');
  const [showBenchmark, setShowBenchmark] = useState(true);

  const timeframes = [
    { key: '7d', label: '7D' },
    { key: '30d', label: '30D' },
    { key: '90d', label: '3M' },
    { key: '1y', label: '1Y' },
    { key: 'all', label: 'All' }
  ];

  const getFilteredData = () => {
    const now = new Date();
    let startDate;
    
    switch (timeframe) {
      case '7d':
        startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case '30d':
        startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        break;
      case '90d':
        startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
        break;
      case '1y':
        startDate = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
        break;
      default:
        return performanceData;
    }
    
    return performanceData?.filter(item => new Date(item.date) >= startDate);
  };

  const chartData = getFilteredData();

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })?.format(value);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date?.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: timeframe === '1y' || timeframe === 'all' ? 'numeric' : undefined
    });
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-popover border border-border rounded-md p-4 shadow-elevation-2">
          <p className="text-sm font-medium text-popover-foreground mb-2">
            {formatDate(label)}
          </p>
          {payload?.map((entry, index) => (
            <div key={index} className="flex items-center justify-between space-x-4">
              <div className="flex items-center space-x-2">
                <div 
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: entry?.color }}
                />
                <span className="text-sm text-muted-foreground">{entry?.name}:</span>
              </div>
              <span className="text-sm font-medium text-popover-foreground">
                {formatCurrency(entry?.value)}
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  const calculatePerformance = () => {
    if (chartData?.length < 2) return { change: 0, percentage: 0 };
    
    const firstValue = chartData?.[0]?.portfolio;
    const lastValue = chartData?.[chartData?.length - 1]?.portfolio;
    const change = lastValue - firstValue;
    const percentage = (change / firstValue) * 100;
    
    return { change, percentage };
  };

  const performance = calculatePerformance();

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-elevation-1">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-xl font-semibold text-card-foreground mb-2">Portfolio Performance</h2>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <span className="text-sm text-muted-foreground">Period Change:</span>
              <span className={`text-sm font-medium ${
                performance?.change >= 0 ? 'text-success' : 'text-error'
              }`}>
                {performance?.change >= 0 ? '+' : ''}{formatCurrency(performance?.change)}
              </span>
              <span className={`text-sm ${
                performance?.percentage >= 0 ? 'text-success' : 'text-error'
              }`}>
                ({performance?.percentage >= 0 ? '+' : ''}{performance?.percentage?.toFixed(2)}%)
              </span>
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex bg-muted rounded-md p-1">
            {timeframes?.map((tf) => (
              <button
                key={tf?.key}
                onClick={() => setTimeframe(tf?.key)}
                className={`px-3 py-1 text-xs font-medium rounded transition-standard ${
                  timeframe === tf?.key
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {tf?.label}
              </button>
            ))}
          </div>

          <button
            onClick={() => setShowBenchmark(!showBenchmark)}
            className={`flex items-center space-x-2 px-3 py-2 rounded-md text-xs font-medium transition-standard ${
              showBenchmark
                ? 'bg-primary/10 text-primary' :'bg-muted text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name="BarChart3" size={14} />
            <span>Compare to BTC</span>
          </button>
        </div>
      </div>
      <div className="h-80 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis 
              dataKey="date" 
              tickFormatter={formatDate}
              stroke="var(--color-muted-foreground)"
              fontSize={12}
            />
            <YAxis 
              tickFormatter={formatCurrency}
              stroke="var(--color-muted-foreground)"
              fontSize={12}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend 
              formatter={(value) => (
                <span className="text-sm text-card-foreground">{value}</span>
              )}
            />
            <Line
              type="monotone"
              dataKey="portfolio"
              stroke="var(--color-primary)"
              strokeWidth={2}
              dot={false}
              name="Portfolio"
              activeDot={{ r: 4, stroke: 'var(--color-primary)', strokeWidth: 2 }}
            />
            {showBenchmark && (
              <Line
                type="monotone"
                dataKey="bitcoin"
                stroke="var(--color-accent)"
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={false}
                name="Bitcoin"
                activeDot={{ r: 4, stroke: 'var(--color-accent)', strokeWidth: 2 }}
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6 pt-6 border-t border-border">
        <div className="text-center">
          <p className="text-xs text-muted-foreground mb-1">Best Day</p>
          <p className="text-sm font-medium text-success">+$12,450</p>
          <p className="text-xs text-muted-foreground">Dec 15, 2024</p>
        </div>
        <div className="text-center">
          <p className="text-xs text-muted-foreground mb-1">Worst Day</p>
          <p className="text-sm font-medium text-error">-$8,920</p>
          <p className="text-xs text-muted-foreground">Nov 28, 2024</p>
        </div>
        <div className="text-center">
          <p className="text-xs text-muted-foreground mb-1">Avg Daily</p>
          <p className="text-sm font-medium text-card-foreground">+$1,245</p>
          <p className="text-xs text-success">+0.36%</p>
        </div>
        <div className="text-center">
          <p className="text-xs text-muted-foreground mb-1">Volatility</p>
          <p className="text-sm font-medium text-card-foreground">18.5%</p>
          <p className="text-xs text-muted-foreground">30-day</p>
        </div>
      </div>
    </div>
  );
};

export default PerformanceChart;